import { useList } from '@refinedev/core';
import { Link } from 'react-router';

import { ui } from '../ui';

/**
 * Catálogo: todas as séries, publicadas ou não.
 *
 * O admin vê as não publicadas (política "séries admin"); qualquer outra conta só veria as
 * que já estão no ar. Se esta lista vier vazia logo depois de entrar, quase sempre significa
 * que a conta não está marcada com `app_metadata.role = 'admin'` — e a tela diz isso, em vez
 * de deixar a pessoa olhando para o nada.
 */
export type Serie = {
  id: string;
  slug: string;
  title: string;
  age_rating: string;
  feed_order: number | null;
  is_premiere: boolean;
  published_at: string | null;
};

export function formatarData(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(iso));
}

/** Publicada, agendada para o futuro, ou rascunho. */
export function situacao(published_at: string | null): {
  rotulo: string;
  estilo: Record<string, unknown>;
} {
  if (!published_at) return { rotulo: 'Rascunho', estilo: ui.seloNeutro };
  const quando = new Date(published_at).getTime();
  if (quando > Date.now()) return { rotulo: 'Agendada', estilo: ui.seloEspera };
  return { rotulo: 'No ar', estilo: ui.seloOk };
}

export function SeriesList() {
  const { query, result } = useList<Serie>({
    resource: 'series',
    sorters: [{ field: 'feed_order', order: 'asc' }],
    pagination: { pageSize: 100 },
  });

  const { isLoading, error } = query;
  const series = result.data;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <h1 style={ui.h1}>Catálogo</h1>
        <Link to="/series/nova" style={{ ...ui.botao, marginLeft: 'auto', lineHeight: '40px' }}>
          Nova série
        </Link>
      </div>

      {isLoading && <p style={ui.dim}>Carregando…</p>}

      {error && (
        <p style={ui.erro}>
          Não deu para ler o catálogo: {error.message}. Confira se a sua conta está marcada com
          app_metadata.role = &quot;admin&quot; no painel do Supabase.
        </p>
      )}

      {!isLoading && !error && series.length === 0 && (
        <div style={ui.aviso}>
          Nenhuma série ainda. Em desenvolvimento, <code>supabase db reset</code> carrega o catálogo
          de exemplo. Em produção, comece por <Link to="/series/nova">Nova série</Link>.
        </div>
      )}

      {series.length > 0 && (
        <table style={ui.tabela}>
          <thead>
            <tr>
              <th style={ui.th}>Ordem</th>
              <th style={ui.th}>Título</th>
              <th style={ui.th}>Slug</th>
              <th style={ui.th}>Class.</th>
              <th style={ui.th}>Situação</th>
              <th style={ui.th}>Publicação</th>
              <th style={ui.th} />
            </tr>
          </thead>
          <tbody>
            {series.map((s: Serie) => {
              const st = situacao(s.published_at);
              return (
                <tr key={s.id}>
                  <td style={ui.td}>{s.feed_order ?? '—'}</td>
                  <td style={ui.td}>
                    <Link to={`/series/${s.id}`} style={{ color: 'inherit' }}>
                      {s.title}
                    </Link>
                    {s.is_premiere && (
                      <span style={{ ...ui.selo, ...ui.seloEspera, marginLeft: 8 }}>Estreia</span>
                    )}
                  </td>
                  <td style={{ ...ui.td, ...ui.dim }}>{s.slug}</td>
                  <td style={ui.td}>{s.age_rating}</td>
                  <td style={ui.td}>
                    <span style={{ ...ui.selo, ...st.estilo }}>{st.rotulo}</span>
                  </td>
                  <td style={{ ...ui.td, ...ui.dim }}>{formatarData(s.published_at)}</td>
                  <td style={ui.td}>
                    <Link to={`/series/${s.id}`} style={{ ...ui.navLink, color: 'inherit' }}>
                      Abrir
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
